import { z } from "zod";

import { DisplayUomOverrideSchema, DocumentDisplayUomSettingsSchema } from "~/shared/documents/schemas/document-display-uom.schema";
import { EditorSchema, emptyEditorDoc } from "~/utils/types/rich-text";

/** Create-modal field exposure for SO Confirmation (split from PDF toggles). */
export const OcDocumentModalSlotsSchema = z.object({
  orderConfirmationNote: z.boolean().default(false),
  lineProducer: z.boolean().default(false),
  lineQuantityUnits: z.boolean().default(false),
});

export type OcDocumentModalSlots = z.infer<typeof OcDocumentModalSlotsSchema>;

export const emptyOcDocumentModalSlots = (): OcDocumentModalSlots => ({
  orderConfirmationNote: false,
  lineProducer: false,
  lineQuantityUnits: false,
});

/** Org settings at `orgSettingKeys.ocDocumentConfig` (SC-2345). Not an opt-in order document slug. */
export const OcDocumentConfigSchema = z
  .object({
    /** Product line PDF columns on page 1. */
    showProducer: z.boolean().default(false),
    showQuantityUnits: z.boolean().default(false),
    /** Rich-text boilerplate blocks. */
    msdsDisclaimerFooter: EditorSchema.default(emptyEditorDoc()),
    drumHazardWarningBody: EditorSchema.default(emptyEditorDoc()),
    /** Org default for order confirmation note (dedicated OC slot — not CRM entity notes). */
    defaultPickupNote: EditorSchema.default(emptyEditorDoc()),
    /** Page 1 optional blocks — PDF output toggles (create modal uses `modalSlots`). */
    showOrderConfirmationNoteBlock: z.boolean().default(false),
    /** When true, MSDS / safety disclaimer may print on page 1. */
    showMsdsDisclaimer: z.boolean().default(false),
    /** When true, customer pickup block may print when broker opts in on the create modal. */
    showDrumHazardOnPdf: z.boolean().default(false),
    /** When true, brokers may opt in to page 2 on drum orders via the create modal. */
    allowOptionalDrumHazardPage: z.boolean().default(false),
    /** When true, SO Confirmation create modal offers customer pickup (SC-2971). */
    allowCustomerPickup: z.boolean().default(false),
    modalSlots: OcDocumentModalSlotsSchema.default(emptyOcDocumentModalSlots()),
  })
  .merge(DocumentDisplayUomSettingsSchema);

export type OcDocumentConfig = z.infer<typeof OcDocumentConfigSchema>;

const optionalTrimmedString = z.string().trim().optional().default("");

/** Per-line modal overrides for producer and quantity units (SC-2345). */
export const SalesOrderConfirmationLineItemInputSchema = z.object({
  orderItemId: z.string().uuid(),
  description: z.string(),
  producer: optionalTrimmedString,
  quantityUnits: optionalTrimmedString,
});

export type SalesOrderConfirmationLineItemInput = z.infer<typeof SalesOrderConfirmationLineItemInputSchema>;

/** Modal-only fields passed at OC create time; not persisted on the order. */
export const SalesOrderConfirmationModalInputSchema = z.object({
  /** Legacy issuance input accepted for compatibility; PDF policy uses the saved Order field. */
  includeDrumHazardPage: z.boolean().optional(),
  orderConfirmationNote: EditorSchema.optional(),
  /** Legacy issuance input accepted for compatibility; PDF layout uses saved Order.customerPickup. */
  customerPickup: z.boolean().optional(),
  /** Legacy issuance inputs accepted for compatibility; confirmation creation never writes PO instructions. */
  supplierShippingRemarks: z.string().nullable().optional(),
  useCustomSupplierShippingInstructions: z.boolean().optional(),
  lineItems: z.array(SalesOrderConfirmationLineItemInputSchema).optional(),
  displayUomOverride: DisplayUomOverrideSchema,
});

export type SalesOrderConfirmationModalInput = z.infer<typeof SalesOrderConfirmationModalInputSchema>;
